import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { mainNavItems } from '../config/routes';
import './Header.css';

const leftNavItems = mainNavItems.slice(0, 3);  // Women, Men, Commercial
const rightNavItems = mainNavItems.slice(3);    // Journal, About, Become a Model

export default function Header() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sideMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
    setSideDropdowns(new Set());
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  const renderNavItem = (item: typeof mainNavItems[0], showDividerAfter = true) => (
    <React.Fragment key={item.path}>
      <li className={`nav-item ${item.children ? 'has-dropdown' : ''} ${isActive(item.path) ? 'is-active' : ''}`}>
        {item.children ? (
          <>
            <button
              className={`nav-link nav-dropdown-toggle ${isActive(item.path) ? 'is-active' : ''}`}
              onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
              aria-expanded={openDropdown === item.label}
              aria-haspopup="true"
            >
              {item.label}
            </button>
            <ul className={`dropdown-menu ${openDropdown === item.label ? 'is-open' : ''}`} role="menu" aria-label={`${item.label} submenu`}>
              {item.children.map((child) => (
                <li key={child.path} role="none">
                  <Link to={child.path} className={`dropdown-link ${location.pathname === child.path ? 'is-active' : ''}`} role="menuitem">
                    {child.label}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Link to={item.path} className={`nav-link ${isActive(item.path) ? 'is-active' : ''}`}>
            {item.label}
          </Link>
        )}
      </li>
      {showDividerAfter && <li className="nav-divider" aria-hidden="true">|</li>}
    </React.Fragment>
  );

  const [sideDropdowns, setSideDropdowns] = useState<Set<string>>(new Set());

  const toggleSideDropdown = (label: string) => {
    setSideDropdowns((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const renderSideNavItem = (item: typeof mainNavItems[0]) => (
    <li key={item.path} className="side-nav-item">
      {item.children ? (
        <>
          <button
            className={`side-nav-link ${isActive(item.path) ? 'is-active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleSideDropdown(item.label);
            }}
          >
            {item.label}
          </button>
          {sideDropdowns.has(item.label) && (
            <ul className="side-nav-sub">
              {item.children.map((child) => (
                <li key={child.path}>
                  <Link
                    to={child.path}
                    className={`side-nav-sublink ${location.pathname === child.path ? 'is-active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {child.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <Link to={item.path} className={`side-nav-link ${isActive(item.path) ? 'is-active' : ''}`}>
          {item.label}
        </Link>
      )}
    </li>
  );

  return (
    <>
      <header className="site-header" role="banner">
        <a href="#main-content" className="skip-to-content">Skip to main content</a>

        <div className="header-bar" ref={dropdownRef}>
          {/* Burger — left edge */}
          <button
            className="burger-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-controls="side-navigation"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          </button>

          {/* Left nav */}
          <nav className="nav-left" role="navigation" aria-label="Main navigation">
            <ul className="nav-list">
              <li className={`nav-item ${location.pathname === '/' ? 'is-active' : ''}`}>
                <Link to="/" className={`nav-link ${location.pathname === '/' ? 'is-active' : ''}`}>Home</Link>
              </li>
              <li className="nav-divider" aria-hidden="true">|</li>
              {leftNavItems.map((item, i) => renderNavItem(item, true))}
            </ul>
          </nav>

          {/* Logo image — centered */}
          <div className="header-logo">
            <Link to="/" aria-label="D Model Management - Home">
              <img src="/images/logo.png" alt="D Model Management" />
            </Link>
          </div>

          {/* Right nav */}
          <nav className="nav-right" role="navigation" aria-label="Secondary navigation">
            <ul className="nav-list">
              {rightNavItems.map((item, i) => renderNavItem(item, i < rightNavItems.length - 1))}
              <li className="nav-item">
                <a
                  href="https://www.instagram.com/dmanagement/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link nav-social-link"
                  aria-label="D Management on Instagram"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
                  </svg>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Side menu */}
      <div className={`side-menu-overlay ${menuOpen ? 'is-open' : ''}`} onClick={() => setMenuOpen(false)} />
      <nav id="side-navigation" className={`side-menu ${menuOpen ? 'is-open' : ''}`} ref={sideMenuRef} role="navigation" aria-label="Full navigation">
        <div className="side-menu-header">
          <Link to="/" className="side-menu-logo" onClick={() => setMenuOpen(false)}>
            <img src="/images/logo.png" alt="D Model Management" />
          </Link>
        </div>
        <ul className="side-nav-list">
          <li className="side-nav-item">
            <Link to="/" className={`side-nav-link ${location.pathname === '/' ? 'is-active' : ''}`}>Home</Link>
          </li>
          {mainNavItems.map(renderSideNavItem)}
        </ul>
        <div className="side-menu-social">
          <a href="https://www.instagram.com/dmanagement/" target="_blank" rel="noopener noreferrer" aria-label="D Management on Instagram">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="18" height="18">
              <path fill="currentColor" d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
            </svg>
          </a>
        </div>
      </nav>
    </>
  );
}
